import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../api/client';
import { useAuth } from '../state/auth';
import { Badge, Bandeau, Carte, Squelette, Vide } from '../components/ui';
import { useMobile } from '../lib/ecran';
import { dateFr } from '../lib/format';

/**
 * Où en est la flotte, sur un entretien donné.
 *
 * « Si on ouvre Vidange, on trouve les différents véhicules avec leur prochaine
 * vidange. » L'écran manquait : il fallait ouvrir les treize fiches une par une
 * pour savoir laquelle allait passer au garage.
 *
 * Le même écran sert au contrôle technique — c'est la même question, posée sur
 * une date plutôt que sur un kilométrage.
 */
type Niveau = 'depasse' | 'proche' | 'ok' | 'jamais';

interface Ligne {
  vehicleId: string;
  immatriculation: string;
  libelle: string;
  km?: number;
  dernierLe?: string | null;
  dernierKm?: number | null;
  prochainKm?: number | null;
  resteKm?: number | null;
  prochaineDate?: string | null;
  dateExpiration?: string | null;
  resteJours?: number | null;
  numero?: string;
  niveau: Niveau;
  texte: string;
}
interface Reponse { libelle?: string; periodiciteKm?: number; aTraiter: number; lignes: Ligne[] }

const BADGE: Record<Niveau, { statut: string; libelle: string }> = {
  depasse: { statut: 'retard', libelle: 'Dépassée' },
  proche: { statut: 'retour', libelle: 'Bientôt' },
  jamais: { statut: 'option', libelle: 'Jamais faite' },
  ok: { statut: 'disponible', libelle: 'À jour' },
};

export default function Echeances({ mode }: { mode: 'entretien' | 'controle' }) {
  const { cle } = useParams();
  const { peut } = useAuth();
  const mobile = useMobile();
  const [d, setD] = useState<Reponse | null>(null);
  const [erreur, setErreur] = useState('');

  const chemin = mode === 'controle' ? '/operations/controle-technique' : `/operations/echeances/${cle}`;

  const charger = useCallback(() => {
    setD(null); setErreur('');
    api.get<Reponse>(chemin).then(setD).catch(() => setErreur("Cette liste n'a pas pu être chargée."));
  }, [chemin]);
  useEffect(charger, [charger]);

  if (erreur) return <Bandeau type="ko" titre="Chargement impossible">{erreur}</Bandeau>;
  if (!d) return <Squelette lignes={6} />;

  const titre = mode === 'controle' ? 'Contrôle technique' : (d.libelle ?? 'Entretien');
  const parNiveau = (n: Niveau) => d.lignes.filter((l) => l.niveau === n);
  const urgentes = [...parNiveau('depasse'), ...parNiveau('proche')];

  return (
    <>
      <div className="entete">
        <div className="titres">
          <h1>{titre}</h1>
          <p>
            {mode === 'controle'
              ? 'Une voiture sans contrôle valide ne peut plus rouler du jour au lendemain'
              : d.periodiciteKm
                ? `Toutes les ${d.periodiciteKm.toLocaleString('fr-FR')} km · une ligne par voiture, la plus urgente en premier`
                : 'Une ligne par voiture, la plus urgente en premier'}
          </p>
        </div>
        {peut('modifier.vehicule') && mode === 'entretien' && (
          <div className="actions">
            <Link className="btn btn-p" to="/entretien">Enregistrer une intervention</Link>
          </div>
        )}
      </div>

      {urgentes.length > 0 ? (
        <div style={{ marginBottom: 16 }}>
          <Bandeau type={parNiveau('depasse').length > 0 ? 'ko' : 'warn'}
                   titre={`${urgentes.length} voiture(s) à traiter`}>
            {parNiveau('depasse').length > 0 && (
              <><b>{parNiveau('depasse').length} dépassée(s)</b> — {parNiveau('depasse').map((l) => l.immatriculation).join(', ')}. </>
            )}
            {parNiveau('proche').length > 0 && (
              <>{parNiveau('proche').length} arrive(nt) à échéance : {parNiveau('proche').map((l) => l.immatriculation).join(', ')}.</>
            )}
          </Bandeau>
        </div>
      ) : (
        <div style={{ marginBottom: 16 }}>
          <Bandeau type="ok" titre="Rien à traiter">
            Aucune voiture n'approche de son échéance
            {mode === 'entretien' && ' — le seuil d’alerte est de 1 000 km'}.
          </Bandeau>
        </div>
      )}

      <Carte pad={false}>
        {d.lignes.length === 0 ? (
          <div className="carte-p"><Vide titre="Aucune voiture dans la flotte" /></div>
        ) : mobile ? (
          <div className="cartes">
            {d.lignes.map((l) => (
              <Link key={l.vehicleId} className="carte-ligne" to="/flotte">
                <div className="cl-tete">
                  <span className="cl-titre">{l.libelle}</span>
                  <Badge statut={BADGE[l.niveau].statut} libelle={BADGE[l.niveau].libelle} />
                </div>
                <div className="cl-sous">{l.immatriculation}{l.km != null && ` · ${l.km.toLocaleString('fr-FR')} km`}</div>
                <div className="cl-pied" style={{ color: l.niveau === 'depasse' ? 'var(--danger)' : undefined }}>
                  {l.texte}
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="enveloppe-tbl">
            <table className="tbl">
              <thead>
                {mode === 'controle' ? (
                  <tr>
                    <th>Véhicule</th><th>N° de contrôle</th><th>Expire le</th>
                    <th>Échéance</th><th>État</th>
                  </tr>
                ) : (
                  <tr>
                    <th>Véhicule</th><th className="droite">Compteur</th>
                    <th>Dernière</th><th className="droite">Prochaine</th>
                    <th>Échéance</th><th>État</th>
                  </tr>
                )}
              </thead>
              <tbody>
                {d.lignes.map((l) => {
                  const rouge = l.niveau === 'depasse';
                  const orange = l.niveau === 'proche';
                  return (
                    <tr key={l.vehicleId}
                        style={{ background: rouge ? 'var(--danger-doux)' : orange ? 'var(--attention-doux)' : undefined }}>
                      <td><b>{l.libelle}</b><div className="mini sourdine">{l.immatriculation}</div></td>
                      {mode === 'controle' ? (
                        <>
                          <td className="mini">{l.numero ?? '—'}</td>
                          <td>{l.dateExpiration ? dateFr(l.dateExpiration) : '—'}</td>
                        </>
                      ) : (
                        <>
                          <td className="droite num">{l.km?.toLocaleString('fr-FR') ?? '—'}</td>
                          <td className="mini sourdine">
                            {l.dernierLe
                              ? <>{dateFr(l.dernierLe)}<div>{l.dernierKm?.toLocaleString('fr-FR')} km</div></>
                              : '—'}
                          </td>
                          <td className="droite num">{l.prochainKm?.toLocaleString('fr-FR') ?? '—'}</td>
                        </>
                      )}
                      <td style={{ fontWeight: rouge || orange ? 600 : 400,
                                   color: rouge ? 'var(--danger)' : orange ? 'var(--attention)' : 'var(--texte2)' }}>
                        {l.texte}
                      </td>
                      <td><Badge statut={BADGE[l.niveau].statut} libelle={BADGE[l.niveau].libelle} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Carte>

      <p className="mini sourdine" style={{ marginTop: 12 }}>
        {mode === 'entretien'
          ? <>Une voiture passe en rouge <b>sous les 1 000 km</b> : c'est la distance qu'elle peut
             faire en une seule location. Le kilométrage vient des relevés saisis sur l'écran
             « Mes voitures » et des états des lieux.</>
          : <>Le contrôle technique est aussi visible sur la fiche de chaque voiture, avec l'assurance
             et la vignette. Une voiture dont le contrôle a expiré ne doit plus sortir.</>}
      </p>
    </>
  );
}
